import React, { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { formatPercent } from '../../utils/behaviourTracking';

const PIE_COLORS = [
  '#B8130F',
  '#E07A5F',
  '#E9C46A',
  '#2A9D8F',
  '#264653',
  '#F4A261',
  '#81B29A',
  '#3D405B',
  '#D62828',
  '#457B9D',
];

function toPercentNumber(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const parsed = Number(String(value).replace('%', '').trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function PieTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload;
  if (!item) return null;
  return (
    <div className="rounded-lg border border-gray-100 bg-white px-3 py-2 text-sm shadow-md">
      <p className="font-semibold text-gray-800">{item.name}</p>
      <p className="text-gray-600">
        Occurrences: {item.count}
        {item.percentage != null ? ` · ${formatPercent(item.percentage)}` : ''}
      </p>
    </div>
  );
}

function BehaviourPerformanceModal({ open, performance, onClose, subtitle, isLoading }) {
  const behaviours = Array.isArray(performance?.behaviours) ? performance.behaviours : [];
  const overall = performance?.overallPercentage;
  const totalNonCompliance = Number(performance?.totalNonCompliance ?? 0);

  const chartData = useMemo(
    () =>
      behaviours.map((item, idx) => {
        const count = Number(item.non_compliance_count ?? 0);
        const percentage = toPercentNumber(item.percentage);
        return {
          id: item.behaviour_id ?? idx,
          name: item.behaviour_name || `Behaviour ${idx + 1}`,
          count: Number.isFinite(count) ? count : 0,
          percentage,
          // Pie needs a positive slice; zero-count items share a tiny equal weight.
          value: count > 0 ? count : 0.0001,
          color: PIE_COLORS[idx % PIE_COLORS.length],
        };
      }),
    [behaviours]
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white px-5 py-5 shadow-2xl sm:px-6 sm:py-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Performance</h3>
            {subtitle ? <p className="mt-0.5 text-sm text-gray-600">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
            aria-label="Close performance"
          >
            ×
          </button>
        </div>

        {isLoading ? (
          <p className="py-10 text-center text-sm text-gray-500">Loading performance…</p>
        ) : chartData.length ? (
          <>
            <div className="relative mx-auto h-56 w-56 sm:h-64 sm:w-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius="58%"
                    outerRadius="85%"
                    paddingAngle={2}
                    stroke="#fff"
                    strokeWidth={2}
                  >
                    {chartData.map((entry) => (
                      <Cell key={entry.id} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <p className="text-xs text-gray-500">Compliance</p>
                <p className="text-2xl font-semibold text-brand">{formatPercent(overall)}</p>
                <p className="mt-0.5 text-[11px] text-gray-500">
                  NC: {Number.isFinite(totalNonCompliance) ? totalNonCompliance : 0}
                </p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
              {chartData.map((item) => (
                <div key={item.id} className="flex min-w-0 items-center gap-2 text-sm">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: item.color }}
                    aria-hidden
                  />
                  <span className="truncate text-gray-700">
                    {item.name}
                    <span className="ml-1 font-medium text-gray-900">{item.count}</span>
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="py-10 text-center text-sm text-gray-500">No performance data found.</p>
        )}

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default BehaviourPerformanceModal;
